<template>
  <div class="dialog-overlay" @click.self="handleClose">
    <div class="dialog-container">
      <div class="dialog-header">
        <h3>시책 관리</h3>
        <button class="btn-close" @click="handleClose">
          <i class="ph ph-x"></i>
        </button>
      </div>

      <div class="dialog-toolbar">
        <div class="toolbar-left">
          <!-- 뷰 토글 -->
          <div class="view-toggle">
            <button 
              class="toggle-btn" 
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <i class="ph ph-list"></i>
            </button>
            <button 
              class="toggle-btn" 
              :class="{ active: viewMode === 'calendar' }"
              @click="viewMode = 'calendar'"
            >
              <i class="ph ph-calendar-blank"></i>
            </button>
          </div>

          <!-- 리스트뷰 필터 (생명/손해) -->
          <div v-if="viewMode === 'list'" class="filter-tabs">
            <button 
              v-for="tab in companyTypeTabs" 
              :key="tab.value"
              class="tab-btn"
              :class="{ active: currentCompanyType === tab.value }"
              @click="currentCompanyType = tab.value"
            >
              {{ tab.label }}
              <span class="tab-count">{{ getCompanyTypeCount(tab.value) }}</span>
            </button>
          </div>

          <!-- 캘린더뷰 월 네비게이션 -->
          <div v-if="viewMode === 'calendar'" class="month-nav">
            <button class="nav-btn" @click="prevMonth">
              <i class="ph ph-caret-left"></i>
            </button>
            <span class="month-label">{{ currentYear }}년 {{ currentMonth + 1 }}월</span>
            <button class="nav-btn" @click="nextMonth">
              <i class="ph ph-caret-right"></i>
            </button>
            <button class="nav-btn today" @click="goToday">오늘</button>
          </div>
        </div>

        <div class="toolbar-right">
          <div class="search-box" v-if="viewMode === 'list'">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="시책명 검색"
            />
          </div>
          <button class="btn btn-primary" @click="openFormDialog(null)">
            <i class="ph ph-plus"></i>
            <span>등록</span>
          </button>
        </div>
      </div>

      <div class="dialog-body">
        <div v-if="loading" class="loading-state">
          <i class="ph ph-spinner spinning"></i>
          <span>불러오는 중...</span>
        </div>

        <!-- 리스트 뷰 -->
        <template v-else-if="viewMode === 'list'">
          <div v-if="filteredPolicies.length === 0" class="empty-state">
            <i class="ph ph-clipboard-text"></i>
            <p>등록된 시책이 없습니다</p>
          </div>

          <div v-else class="policy-list">
            <div 
              v-for="policy in filteredPolicies" 
              :key="policy.idx"
              class="policy-card"
              :class="getStatusClass(policy)"
              @click="openViewDialog(policy)"
            >
              <!-- 회사 로고 (short_img) -->
              <div class="policy-logo">
                <img 
                  v-if="policy.short_img" 
                  :src="policy.short_img" 
                  :alt="policy.company_name"
                />
                <div v-else class="logo-placeholder">
                  {{ policy.company_name?.charAt(0) || '?' }}
                </div>
              </div>

              <div class="policy-content">
                <div class="policy-top">
                  <span class="company-name">{{ policy.company_name }}</span>
                  <span class="status-badge" :class="getStatusClass(policy)">
                    {{ getStatusText(policy) }}
                  </span>
                </div>
                <h4 class="policy-title">{{ policy.title }}</h4>
                <div class="policy-meta">
                  <span class="meta-item">
                    <i class="ph ph-coins"></i>
                    {{ formatNumber(policy.amount) }}%
                  </span>
                  <span class="meta-item">
                    <i class="ph ph-calendar"></i>
                    {{ formatDateRange(policy.start_date, policy.expire_date) }}
                  </span>
                </div>
              </div>

              <!-- 시책 이미지 (A4 비율) -->
              <!-- <div class="policy-image-wrapper" v-if="policy.image">
                <img :src="policy.image" alt="" class="policy-image" />
              </div> -->

              <div class="goldbar-wrapper">
                <h2>최대 <b>{{ formatNumber(policy.amount || 0) }}%</b></h2>
              </div>

              <div class="policy-actions">
                <button class="btn-icon" @click.stop="openFormDialog(policy)" title="수정">
                  <i class="ph ph-pencil-simple"></i>
                </button>
                <button class="btn-icon danger" @click.stop="confirmDelete(policy)" title="삭제">
                  <i class="ph ph-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- 캘린더 뷰 (간트 차트) -->
        <template v-else-if="viewMode === 'calendar'">
          <div v-if="policies.length === 0" class="empty-state">
            <i class="ph ph-clipboard-text"></i>
            <p>등록된 시책이 없습니다</p>
          </div>

          <div v-else class="gantt-container">
            <!-- 헤더: 날짜 -->
            <div class="gantt-header">
              <div class="gantt-label-col">시책</div>
              <div class="gantt-dates">
                <div 
                  v-for="day in daysInMonth" 
                  :key="day"
                  class="gantt-date-cell"
                  :class="{ 
                    weekend: isWeekend(day),
                    today: isToday(day)
                  }"
                >
                  <span class="day-num">{{ day }}</span>
                  <span class="day-name">{{ getDayName(day) }}</span>
                </div>
              </div>
            </div>

            <!-- 바디: 시책별 타임라인 -->
            <div class="gantt-body">
              <div 
                v-for="policy in sortedPoliciesForCalendar" 
                :key="policy.idx"
                class="gantt-row"
              >
                <div class="gantt-label-col" @click="openViewDialog(policy)">
                  <div class="policy-info">
                    <img 
                      v-if="policy.short_img" 
                      :src="policy.short_img" 
                      class="company-logo-mini"
                    />
                    <div v-else class="company-logo-mini placeholder">
                      {{ policy.company_name?.charAt(0) || '?' }}
                    </div>
                    <div class="policy-text">
                      <span class="policy-name">{{ policy.title }}</span>
                      <span class="policy-amount">{{ policy.amount }}%</span>
                    </div>
                  </div>
                </div>
                <div class="gantt-dates">
                  <div 
                    v-for="day in daysInMonth" 
                    :key="day"
                    class="gantt-cell"
                    :class="{ 
                      weekend: isWeekend(day),
                      today: isToday(day)
                    }"
                  >
                  </div>
                  <!-- 시책 바 -->
                  <div 
                    v-if="getPolicyBar(policy)"
                    class="policy-bar"
                    :class="getStatusClass(policy)"
                    :style="getPolicyBar(policy)"
                    @click="openViewDialog(policy)"
                  >
                    <span class="bar-label">{{ policy.title }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 범례 -->
            <div class="gantt-legend">
              <div class="legend-item">
                <span class="legend-color active"></span>
                <span>진행중</span>
              </div>
              <div class="legend-item">
                <span class="legend-color upcoming"></span>
                <span>예정</span>
              </div>
              <div class="legend-item">
                <span class="legend-color ended"></span>
                <span>종료</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="dialog-footer">
        <div class="summary-stats">
          <div class="stat-item">
            <span class="stat-label">전체</span>
            <span class="stat-value">{{ policies.length }}건</span>
          </div>
          <div class="stat-item active">
            <span class="stat-label">진행중</span>
            <span class="stat-value">{{ getStatusCount('active') }}건</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">총 시책금액</span>
            <span class="stat-value highlight">{{ formatNumber(totalAmount) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 상세보기 -->
    <PolicyViewDialog
      v-if="viewDialogVisible"
      :policy="selectedPolicy"
      @close="viewDialogVisible = false"
      @edit="handleViewEdit"
      @delete="handleViewDelete"
    />

    <!-- 등록/수정 -->
    <PolicyEditDialog
      v-if="formDialogVisible"
      :edit-data="editingPolicy"
      :companies="companies"
      @close="formDialogVisible = false"
      @submit="handleFormSubmit"
    />
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, defineProps } from 'vue'
import PolicyEditDialog from './policyEdit.vue'
import PolicyViewDialog from './policyView.vue'

const props = defineProps({
  oth_showPolicyList: { type: Function, required: true }
})

const sql_query = inject('sql_query')
const member_info = inject('member_info')

const loading = ref(false)
const policies = ref([])
const companies = ref([])
const searchQuery = ref('')
const currentCompanyType = ref('all')
const viewMode = ref('list') // 'list' | 'calendar'

// 캘린더용 상태
const today = new Date()
const currentYear = ref(today.getFullYear())
const currentMonth = ref(today.getMonth())

const formDialogVisible = ref(false)
const editingPolicy = ref(null)
const viewDialogVisible = ref(false)
const selectedPolicy = ref(null)

// 회사 타입 필터 탭
const companyTypeTabs = [
  { label: '전체', value: 'all' },
  { label: '생명', value: 'life' },
  { label: '손해', value: 'fire' }
]

// 캘린더 관련 computed
const daysInMonth = computed(() => {
  return new Date(currentYear.value, currentMonth.value + 1, 0).getDate()
})

const sortedPoliciesForCalendar = computed(() => {
  return [...policies.value].sort((a, b) => {
    const order = { active: 0, upcoming: 1, ended: 2 }
    const statusDiff = order[getStatus(a)] - order[getStatus(b)]
    if (statusDiff !== 0) return statusDiff
    return new Date(a.start_date) - new Date(b.start_date)
  })
})

function isWeekend(day) {
  const date = new Date(currentYear.value, currentMonth.value, day)
  const dayOfWeek = date.getDay()
  return dayOfWeek === 0 || dayOfWeek === 6
}

function isToday(day) {
  const t = new Date()
  return (
    t.getFullYear() === currentYear.value &&
    t.getMonth() === currentMonth.value &&
    t.getDate() === day
  )
}

function getDayName(day) {
  const date = new Date(currentYear.value, currentMonth.value, day)
  const names = ['일', '월', '화', '수', '목', '금', '토']
  return names[date.getDay()]
}

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
}

function goToday() {
  const t = new Date()
  currentYear.value = t.getFullYear()
  currentMonth.value = t.getMonth()
}

function getPolicyBar(policy) {
  const start = new Date(policy.start_date)
  const end = new Date(policy.expire_date)
  
  const monthStart = new Date(currentYear.value, currentMonth.value, 1)
  const monthEnd = new Date(currentYear.value, currentMonth.value + 1, 0)
  
  // 현재 월과 겹치지 않으면 null
  if (end < monthStart || start > monthEnd) return null
  
  // 시작/끝 위치 계산
  const barStart = start < monthStart ? 1 : start.getDate()
  const barEnd = end > monthEnd ? daysInMonth.value : end.getDate()
  
  const cellWidth = 100 / daysInMonth.value
  const left = (barStart - 1) * cellWidth
  const width = (barEnd - barStart + 1) * cellWidth
  
  return {
    left: `${left}%`,
    width: `${width}%`
  }
}

onMounted(async () => {
  await Promise.all([fetchPolicies(), fetchCompanies()])
})

async function fetchPolicies() {
  loading.value = true
  try {
    const sql = `
      SELECT 
        p.idx, p.company_idx, p.title, p.description, p.goal, 
        p.amount, p.start_date, p.expire_date, p.mb_id, p.image, p.extra_images, p.weeks, p.created_at,
        c.name as company_name, c.short_img, c.type as company_type
      FROM insu_policies p
      LEFT JOIN insu_company c ON p.company_idx = c.idx
      ORDER BY p.start_date DESC
    `;

    policies.value = await sql_query(sql)
  } finally {
    loading.value = false
  }
}

async function fetchCompanies() {
  const sql = `SELECT * FROM insu_company ORDER BY sort_no, name`
  companies.value = await sql_query(sql)
}

function getStatus(policy) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = new Date(policy.start_date)
  const end = new Date(policy.expire_date)
  
  if (today < start) return 'upcoming'
  if (today > end) return 'ended'
  return 'active'
}

function getStatusClass(policy) {
  return getStatus(policy)
}

function getStatusText(policy) {
  const map = { upcoming: '예정', active: '진행중', ended: '종료' }
  return map[getStatus(policy)]
}

function getStatusCount(status) {
  if (status === 'all') return policies.value.length
  return policies.value.filter(p => getStatus(p) === status).length
}

// 회사 타입별 카운트
function getCompanyTypeCount(type) {
  if (type === 'all') return policies.value.length
  return policies.value.filter(p => p.company_type === type).length
}

const filteredPolicies = computed(() => {
  let result = [...policies.value]
  
  // 회사 타입 필터
  if (currentCompanyType.value !== 'all') {
    result = result.filter(p => p.company_type === currentCompanyType.value)
  }
  
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(p => 
      p.title.toLowerCase().includes(query) ||
      p.company_name?.toLowerCase().includes(query)
    )
  }
  
  const order = { active: 0, upcoming: 1, ended: 2 }
  result.sort((a, b) => {
    const statusDiff = order[getStatus(a)] - order[getStatus(b)]
    if (statusDiff !== 0) return statusDiff
    return new Date(b.start_date) - new Date(a.start_date)
  })
  
  return result
})

const totalAmount = computed(() => {
  return policies.value
    .filter(p => getStatus(p) !== 'ended')
    .reduce((sum, p) => sum + (p.amount || 0), 0)
})

function formatNumber(num) {
  return num?.toLocaleString('ko-KR') || '0'
}

function formatDateRange(start, end) {
  const s = new Date(start)
  const e = new Date(end)
  const format = d => `${d.getMonth() + 1}/${d.getDate()}`
  return `${format(s)} ~ ${format(e)}`
}

function handleClose() {
  props.oth_showPolicyList(false)
}

function openViewDialog(policy) {
  selectedPolicy.value = policy
  viewDialogVisible.value = true
}

function handleViewEdit(policy) {
  viewDialogVisible.value = false
  openFormDialog(policy)
}

async function handleViewDelete(policy) {
  viewDialogVisible.value = false
  await confirmDelete(policy)
}

function openFormDialog(policy) {
  editingPolicy.value = policy
  formDialogVisible.value = true
}

function formatDateForDB(dateStr) {
  if (!dateStr) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }
  const d = new Date(dateStr)
  return d.toISOString().slice(0, 10)
}

function escapeSQL(str) {
  if (!str) return ''
  return String(str).replace(/'/g, "''")
}

async function handleFormSubmit(formData) {
  const title = escapeSQL(formData.title)
  const description = escapeSQL(formData.description)
  const goal = escapeSQL(formData.goal)
  const image = escapeSQL(formData.image)
  const extra_images = escapeSQL(formData.extra_images)
  const weeks = escapeSQL(formData.weeks)  // 추가
  const startDate = formatDateForDB(formData.start_date)
  const expireDate = formatDateForDB(formData.expire_date)

  let sql = '';

  if (formData.isEdit) {
    sql = `
      UPDATE insu_policies SET
        company_idx = ${formData.company_idx},
        title = '${title}',
        description = '${description}',
        goal = '${goal}',
        amount = ${formData.amount},
        start_date = '${startDate}',
        expire_date = '${expireDate}',
        image = '${image}',
        extra_images = '${extra_images}',
        weeks = '${weeks}'
      WHERE idx = ${formData.idx}
    `
  } else {
    sql = `
      INSERT INTO insu_policies (company_idx, title, description, goal, amount, start_date, expire_date, mb_id, image, extra_images, weeks)
      VALUES (
        ${formData.company_idx}, 
        '${title}', 
        '${description}', 
        '${goal}', 
        ${formData.amount}, 
        '${startDate}', 
        '${expireDate}', 
        '${member_info.mb_id}', 
        '${image}',
        '${extra_images}',
        '${weeks}'
      )
    `
  }
  console.log(sql);
  await sql_query(sql);
  
  formDialogVisible.value = false
  await fetchPolicies()
}

async function confirmDelete(policy) {
  if (!confirm(`"${policy.title}" 시책을 삭제하시겠습니까?`)) return
  
  await sql_query(`DELETE FROM insu_policies WHERE idx = ${policy.idx}`)
  await fetchPolicies()
}
</script>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spinning {
  animation: spin 1s linear infinite;
}

.dialog-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-middle);
  padding: 20px;
  box-sizing: border-box;
}

.dialog-container {
  background: var(--color-background);
  border-radius: var(--radius-lg);
  width: 100%;
  height: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--color-surface-reverse);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-surface-reverse);
}

.dialog-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  background: var(--color-background-light);
  color: var(--color-text-primary);
}

.dialog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: var(--color-surface-light);
  border-bottom: 1px solid var(--color-surface-reverse);
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.view-toggle {
  display: flex;
  background: var(--color-background);
  border: 1px solid var(--color-surface-reverse);
  border-radius: 10px;
  padding: 4px;
}

.toggle-btn {
  width: 36px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.toggle-btn:hover {
  color: var(--color-text-primary);
}

.toggle-btn.active {
  background: var(--color-brand);
  color: #fff;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  background: var(--color-background);
  padding: 4px;
  border-radius: 10px;
  border: 1px solid var(--color-surface-reverse);
}

.tab-btn {
  padding: 8px 14px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tab-btn:hover {
  color: var(--color-text-primary);
}

.tab-btn.active {
  background: var(--color-brand);
  color: #fff;
}

.tab-count {
  font-size: var(--font-size-xs);
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.08);
}

.tab-btn.active .tab-count {
  background: rgba(255, 255, 255, 0.2);
}

/* 월 네비게이션 */
.month-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--color-background);
  border: 1px solid var(--color-surface-reverse);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.nav-btn:hover {
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
}

.nav-btn.today {
  width: auto;
  padding: 0 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.month-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  min-width: 100px;
  text-align: center;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--color-background);
  border: 1px solid var(--color-surface-reverse);
  border-radius: var(--radius-md);
}

.search-box i {
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
}

.search-box input {
  border: none;
  outline: none;
  font-size: var(--font-size-base);
  width: 140px;
  background: transparent;
  color: var(--color-text-primary);
}

.btn {
  padding: 9px 16px;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--color-brand);
  color: #fff;
}

.btn-primary:hover {
  filter: brightness(1.1);
}

.dialog-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);
  gap: 12px;
}

.loading-state i,
.empty-state i {
  font-size: var(--font-size-2xl);
}

.empty-state p {
  margin: 0;
  font-size: var(--font-size-base);
}

/* 리스트 뷰 */
.policy-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.policy-card {
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 16px;
  /* background: var(--color-surface-light); */
  background-image: url('~@/../public/imgs/common/golden_wide_bg.png');
  background-size: cover;
  background-position: center;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.2s;
  min-height: 88px;
}

.policy-card:hover {
  border-color: var(--color-brand);
  box-shadow: 0 4px 12px rgba(255, 165, 0, 0.15);
}

.policy-card.ended {
  opacity: 0.5;
}

.policy-card.ended:hover {
  opacity: 0.7;
}

.policy-logo {
  width: 60px;
  height: 60px;
  border-radius:12px;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.policy-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius:12px;
}

.logo-placeholder {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
}

.policy-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.policy-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.company-name {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.status-badge {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 3px 8px;
  border-radius: 12px;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.status-badge.upcoming {
  background: rgba(51, 170, 255, 0.15);
  color: var(--color-special-point);
}

.status-badge.ended {
  background: var(--color-surface-reverse);
  color: var(--color-text-secondary);
}

.policy-title {
  margin: 0 0 8px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.policy-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.meta-item i {
  font-size: var(--font-size-sm);
  color: var(--color-text-third);
}

/* 시책 이미지 (A4 비율: 210:297 ≈ 1:1.414) */
.policy-image-wrapper {
  flex-shrink: 0;
  height: 100%;
  max-height:90px;
  aspect-ratio: 210 / 297;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
}

.policy-image {
  width: auto;
  height: 100%;
  object-fit: contain;
}

.policy-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s;
  align-self: center;
}

.policy-card:hover .policy-actions {
  opacity: 1;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background-light);
  color: var(--color-text-primary);
}

.btn-icon:hover {
}

.btn-icon.danger:hover {
  background: rgba(218, 47, 71, 0.15);
  color: var(--color-special-warning);
}

/* 간트 차트 */
.gantt-container {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.gantt-header {
  display: flex;
  border-bottom: 2px solid var(--color-surface-reverse);
  background: var(--color-surface-light);
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.gantt-label-col {
  width: 180px;
  min-width: 180px;
  padding: 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-surface-reverse);
  display: flex;
  align-items: center;
}

.gantt-dates {
  flex: 1;
  display: flex;
  position: relative;
  min-width: 0;
}

.gantt-date-cell {
  flex: 1;
  min-width: 28px;
  padding: 8px 2px;
  text-align: center;
  border-right: 1px solid var(--color-surface-reverse);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gantt-date-cell:last-child {
  border-right: none;
}

.gantt-date-cell.weekend {
  background: rgba(0, 0, 0, 0.03);
}

.gantt-date-cell.today {
  background: rgba(255, 165, 0, 0.1);
}

.day-num {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.gantt-date-cell.weekend .day-num {
  color: var(--color-special-warning);
}

.gantt-date-cell.today .day-num {
  color: var(--color-brand);
}

.day-name {
  font-size: 10px;
  color: var(--color-text-third);
}

.gantt-body {
  display: flex;
  flex-direction: column;
}

.gantt-row {
  display: flex;
  border-bottom: 1px solid var(--color-surface-reverse);
  transition: background 0.2s;
}

.gantt-row:hover {
  background: var(--color-surface-light);
}

.gantt-row .gantt-label-col {
  cursor: pointer;
  font-weight: var(--font-weight-medium);
}

.gantt-row .gantt-label-col:hover {
  background: var(--color-surface-reverse);
}

.policy-info {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.company-logo-mini {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: contain;
  background: var(--color-background);
  flex-shrink: 0;
}

.company-logo-mini.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
}

.policy-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.policy-name {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.policy-amount {
  font-size: 11px;
  color: var(--color-brand);
  font-weight: var(--font-weight-semibold);
}

.gantt-cell {
  flex: 1;
  min-width: 28px;
  height: 52px;
  border-right: 1px solid var(--color-surface-reverse);
}

.gantt-cell:last-child {
  border-right: none;
}

.gantt-cell.weekend {
  background: rgba(0, 0, 0, 0.02);
}

.gantt-cell.today {
  background: rgba(255, 165, 0, 0.08);
}

.policy-bar {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 8px;
  transition: all 0.2s;
  z-index: 5;
}

.policy-bar:hover {
  filter: brightness(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.policy-bar.active {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.policy-bar.upcoming {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.policy-bar.ended {
  background: linear-gradient(90deg, #9ca3af, #d1d5db);
}

.bar-label {
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.gantt-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 16px;
  border-top: 1px solid var(--color-surface-reverse);
  background: var(--color-surface-light);
  border-radius: 0 0 var(--radius-md) var(--radius-md);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.legend-color.active {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.legend-color.upcoming {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.legend-color.ended {
  background: linear-gradient(90deg, #9ca3af, #d1d5db);
}

/* 푸터 */
.dialog-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--color-surface-reverse);
  background: var(--color-surface-light);
}

.summary-stats {
  display: flex;
  gap: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.stat-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.stat-value.highlight {
  color: var(--color-brand);
}

.stat-item.active .stat-value {
  color: #10b981;
}

.goldbar-wrapper {
  width: 300px;
  height: 84px;
  background-image: url('~@/../public/imgs/common/goldbar.png');
  background-position: center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 1px, rgba(0, 0, 0, 0.12) 0px 2px 2px, rgba(0, 0, 0, 0.12) 0px 4px 4px, rgba(0, 0, 0, 0.12) 0px 8px 8px, rgba(0, 0, 0, 0.12) 0px 16px 16px;
}

.goldbar-wrapper > h2 {
  margin-bottom: 36px;
  font-size: 24px;
  font-weight: 800;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:12px;
  color:#000;
}

.goldbar-wrapper > h2 > b {
  font-size: 48px;
  font-weight: 600;
  letter-spacing: -4px;
}

/* 반응형 */
@media (max-width: 768px) {
  .dialog-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .toolbar-left {
    justify-content: space-between;
  }
  
  .toolbar-right {
    justify-content: flex-end;
  }
  
  .gantt-label-col {
    width: 120px;
    min-width: 120px;
  }
  
  .policy-name {
    font-size: 12px;
  }
  
  .policy-image-wrapper {
    display: none;
  }
}
</style>